import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { handlePostRequest } from '../service/PostTemplate';
import { endPoints } from '../Config';
import { toast } from 'react-toastify';
import usePreferredLanguage from '../utilities/usePreferredLanguage';
import { translateUiText } from '../utilities/siteTranslations';

const ForgetPassword = () => {
    const [step, setStep] = useState('email'); // email -> otp -> newPassword
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);

    const handleSendOtp = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            toast.error(t('Please enter your email.'));
            return;
        }

        try {
            setLoading(true);
            const payload = { email: trimmedEmail };
            const response = await dispatch(handlePostRequest(payload, endPoints.generateOtp, true, false));

            if (response && (response.status === 200 || response.status === 201)) {
                toast.success(response?.data?.message || t('OTP sent to your email.'));
                setStep('otp');
            }
        } catch (error) {
            // error toasts handled in template
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            toast.error(t('Please enter your email.'));
            return;
        }

        try {
            setLoading(true);
            const payload = { email: trimmedEmail };
            const response = await dispatch(handlePostRequest(payload, endPoints.resendOtp, true, false));

            if (response && (response.status === 200 || response.status === 201)) {
                toast.success(response?.data?.message || t('OTP resent successfully.'));
            }
        } catch (error) {
            // error toasts handled in template
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        const trimmedEmail = email.trim();
        const trimmedOtp = otp.trim();

        if (!trimmedEmail) {
            toast.error(t('Please enter your email.'));
            return;
        }
        if (!trimmedOtp) {
            toast.error(t('Please enter the OTP sent to your email.'));
            return;
        }

        try {
            setLoading(true);
            const payload = { email: trimmedEmail, otp: trimmedOtp };
            const response = await dispatch(handlePostRequest(payload, endPoints.verifyOtp, true, false));

            if (response && (response.status === 200 || response.status === 201)) {
                toast.success(response?.data?.message || t('OTP verified successfully.'));
                setStep('newPassword');
            }
        } catch (error) {
            // error toasts handled in template
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        const trimmedEmail = email.trim();
        const trimmedNewPassword = newPassword.trim();
        const trimmedConfirmPassword = confirmPassword.trim();

        if (!trimmedEmail) {
            toast.error(t('Please enter your email.'));
            return;
        }
        if (!trimmedNewPassword) {
            toast.error(t('Please enter a new password.'));
            return;
        }
        if (trimmedNewPassword.length < 8) {
            toast.error(t('Password must be at least 8 characters long.'));
            return;
        }
        if (trimmedNewPassword !== trimmedConfirmPassword) {
            toast.error(t('New password and confirm password do not match.'));
            return;
        }

        try {
            setLoading(true);
            const payload = {
                email: trimmedEmail,
                password: trimmedNewPassword
                // OTP is already verified in previous step (same pattern as profile update password)
            };
            const response = await dispatch(handlePostRequest(payload, endPoints.changePassword, true, false));

            if (response && (response.status === 200 || response.status === 201)) {
                toast.success(response?.data?.message || t('Password reset successfully. Please login with your new password.'));
                navigate('/login');
            }
        } catch (error) {
            // error toasts handled in template
        } finally {
            setLoading(false);
        }
    };

    const renderEmailStep = () => (
        <div className="card p-4" style={{ maxWidth: '420px', width: '100%', color: '#000' }}>
            <h3 className="mb-3">{t('Forgot Password')}</h3>
            <p className="mb-4 text-500" style={{ fontSize: '0.9rem', color: '#000' }}>
                {t('Enter your registered email address to receive a verification code.')}
            </p>
            <div className="mb-3">
                <label htmlFor="email" className="block mb-2">
                    {t('Email')}
                </label>
                <InputText
                    id="email"
                    type="email"
                    className="w-full Login__Input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('Enter your email')}
                    autoComplete="email"
                    disabled={loading}
                />
            </div>
            <div className="flex justify-content-center mt-3">
                <Button
                    label={t('Send OTP')}
                    icon="pi pi-send"
                    onClick={handleSendOtp}
                    loading={loading}
                    disabled={loading}
                    className="Login__Button w-full md:w-10"
                />
            </div>
            <div className="flex justify-content-center mt-3">
                <Button
                    label={t('Back to Login')}
                    className="p-button-text"
                    onClick={() => navigate('/login')}
                    disabled={loading}
                />
            </div>
        </div>
    );

    const renderOtpStep = () => (
        <div className="card p-4" style={{ maxWidth: '420px', width: '100%', color: '#000' }}>
            <h3 className="mb-3">{t('Verify OTP')}</h3>
            <p className="mb-4 text-500" style={{ fontSize: '0.9rem', color: '#000' }}>
                {t('We have sent a 4-digit OTP to')} <strong>{email}</strong>. {t('Please enter it below.')}
            </p>
            <div className="mb-3">
                <label htmlFor="otp" className="block mb-2">
                    {t('OTP')}
                </label>
                <InputText
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={4}
                    className="w-full Login__Input"
                    placeholder={t('Enter 4-digit OTP')}
                    disabled={loading}
                />
            </div>
            <div className="flex justify-content-between mt-3">
                <Button
                    label={t('Back')}
                    className="p-button-text"
                    onClick={() => setStep('email')}
                    disabled={loading}
                />
                <div className="flex gap-2">
                    <Button
                        label={t('Resend OTP')}
                        className="p-button-text"
                        icon="pi pi-refresh"
                        onClick={handleResendOtp}
                        disabled={loading}
                    />
                    <Button
                        label={t('Verify OTP')}
                        icon="pi pi-check"
                        onClick={handleVerifyOtp}
                        loading={loading}
                        disabled={loading}
                        className="Login__Button"
                    />
                </div>
            </div>
        </div>
    );

    const renderNewPasswordStep = () => (
        <div className="card p-4" style={{ maxWidth: '420px', width: '100%', color: '#000' }}>
            <h3 className="mb-3">{t('Reset Password')}</h3>
            <p className="mb-4 text-500" style={{ fontSize: '0.9rem', color: '#000' }}>
                {t('Create a new password for your account.')}
            </p>
            <div className="mb-3">
                <label htmlFor="newPassword" className="block mb-2">
                    {t('New Password')}
                </label>
                <InputText
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full Login__Input"
                    placeholder={t('Enter new password')}
                    disabled={loading}
                />
            </div>
            <div className="mb-3">
                <label htmlFor="confirmPassword" className="block mb-2">
                    {t('Confirm Password')}
                </label>
                <InputText
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full Login__Input"
                    placeholder={t('Re-enter new password')}
                    disabled={loading}
                />
            </div>
            <div className="flex justify-content-between mt-3">
                <Button
                    label={t('Back')}
                    className="p-button-text"
                    onClick={() => setStep('otp')}
                    disabled={loading}
                />
                <Button
                    label={t('Reset Password')}
                    icon="pi pi-save"
                    onClick={handleResetPassword}
                    loading={loading}
                    disabled={loading}
                    className="Login__Button"
                />
            </div>
        </div>
    );

    return (
        <div className="login-full-bg flex align-items-center justify-content-center min-h-screen px-3 py-4">
            <div className="login-overlay" />
            <div className="flex align-items-center justify-content-center w-full" style={{ position: 'relative', zIndex: 1 }}>
                {step === 'email' && renderEmailStep()}
                {step === 'otp' && renderOtpStep()}
                {step === 'newPassword' && renderNewPasswordStep()}
            </div>
        </div>
    );
};

export default ForgetPassword;

